import { FC } from 'react';
import { Typography, Box, Chip, Divider, CardMedia } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { getPost } from '../services/posts';
import Markdown from 'markdown-to-jsx';
import { Link as RouterLink } from 'react-router-dom';
import { toSeoFriendlyUrl } from '../utils/urlHelpers';
import { SeriesNavigation } from './SeriesNavigation';

const BlogPostWrapper = styled(Box)(
  ({ theme }) => `
    background: ${theme.palette.background.paper};
    border-radius: ${theme.general.borderRadius};
    margin-bottom: ${theme.spacing(3)};
    overflow: hidden;
`
);

const FeaturedImage = styled(CardMedia)(
  ({ theme }) => `
    height: 400px;
    background-size: cover;
    background-position: center;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50%;
      background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.1));
    }
`
);

const Content = styled(Box)(
  ({ theme }) => `
    padding: ${theme.spacing(3)};
`
);

const MarkdownWrapper = styled(Box)(
  ({ theme }) => `
    h1 {
      font-size: ${theme.typography.h1.fontSize};
      margin-bottom: ${theme.spacing(2)};
    }
    h2 {
      font-size: ${theme.typography.h2.fontSize};
      margin-bottom: ${theme.spacing(2)};
      margin-top: ${theme.spacing(4)};
    }
    h3 {
      font-size: ${theme.typography.h3.fontSize};
      margin-bottom: ${theme.spacing(2)};
      margin-top: ${theme.spacing(3)};
    }
    p {
      margin-bottom: ${theme.spacing(2)};
    }
    ul, ol {
      margin-bottom: ${theme.spacing(2)};
      padding-left: ${theme.spacing(2)};
    }
    li {
      margin-bottom: ${theme.spacing(1)};
    }
    table {
      border-collapse: collapse;
      display: block;
      margin-bottom: ${theme.spacing(3)};
      max-width: 100%;
      overflow-x: auto;
    }
    th, td {
      border-bottom: 1px solid ${theme.colors.alpha.black[10]};
      padding: ${theme.spacing(1)} ${theme.spacing(2)};
      text-align: left;
      white-space: nowrap;
    }
    th {
      color: ${theme.palette.text.secondary};
      font-weight: bold;
    }
`
);

const TagsWrapper = styled(Box)(
  ({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    margin: ${theme.spacing(2)} 0;
    
    .MuiChip-root {
      margin-bottom: ${theme.spacing(1)};
    }
`
);

export const BlogPost: FC = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <Typography variant="h2">
        Post not found
      </Typography>
    );
  }

  return (
    <>
      <BlogPostWrapper>
        {post.featuredImage && (
          <FeaturedImage
            image={post.featuredImage}
            title={post.title}
          />
        )}
        <Content>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {post.title}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {post.date} • {post.category}
            </Typography>
          </Box>
          <MarkdownWrapper>
            <Markdown>
              {post.content}
            </Markdown>
          </MarkdownWrapper>
          <Divider sx={{ my: 2 }} />
          <TagsWrapper>
            {post.tags.map((tag) => (
              <Chip 
                key={tag} 
                label={tag} 
                size="small"
                component={RouterLink}
                to={`/blog/tag/${toSeoFriendlyUrl(tag)}`}
                clickable
              />
            ))}
          </TagsWrapper>
        </Content>
      </BlogPostWrapper>
      {post.series && <SeriesNavigation currentPost={post} />}
    </>
  );
}; 